import React from 'react';

export type RelatedGuide = {
  href: string;
  label: string;
  /** Image path, e.g. /host/images/discover-images/related-tokyo-culture.webp. Optional; card shows a placeholder if omitted. */
  image?: string;
};

interface RelatedGuidesProps {
  /** Section heading. Default: "Related guides" */
  heading?: string;
  /** Optional icon shown next to the heading. */
  headingIcon?: string;
  guides: RelatedGuide[];
  /** Optional CSS class for the outer nav. */
  className?: string;
  /** Optional CSS class for the heading. When omitted, uses default (text-sm). Use e.g. "text-2xl md:text-3xl font-semibold text-gray-900 mb-3" for a proper section title. */
  headingClassName?: string;
  /** Optional subtitle/paragraph under the heading. */
  subtitle?: string;
  /** Optional CSS class for the subtitle. When omitted with subtitle, uses "text-gray-600 mb-6". */
  subtitleClassName?: string;
}

const RelatedGuides: React.FC<RelatedGuidesProps> = ({
  heading = 'Related guides',
  headingIcon,
  guides,
  className = '',
  headingClassName,
  subtitle,
  subtitleClassName,
}) => {
  if (!guides?.length) return null;

  return (
    <nav className={`mt-8 pt-8 border-t border-gray-200 ${className}`} aria-label={heading}>
      <h2 className={(headingClassName ?? 'text-sm font-semibold text-gray-900 mb-4') + (headingIcon ? ' flex items-center gap-3' : '')}>
        {headingIcon ? (
          <img src={headingIcon} alt="" aria-hidden className="w-10 h-10 flex-shrink-0" />
        ) : null}
        <span>{heading}</span>
      </h2>
      {subtitle && <p className={subtitleClassName ?? 'text-gray-600 mb-6'}>{subtitle}</p>}
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 list-none p-0 m-0">
        {guides.map((guide, index) => (
          <li key={index}>
            <a
              href={guide.href}
              className="group block h-full rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-primary-300 hover:shadow-md transition-all duration-200"
            >
              {/* Image or placeholder */}
              <div className="aspect-[4/3] w-full bg-gray-100 overflow-hidden">
                {guide.image ? (
                  <img
                    src={guide.image}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-200"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-gray-400"
                    aria-hidden="true"
                  >
                    <svg
                      className="w-10 h-10 opacity-40"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              {/* Label */}
              <div className="p-3">
                <span className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {guide.label}
                </span>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default RelatedGuides;
